import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { fetchRssFeedItems } from '../lib/rssParser';
import { playSound } from '../lib/soundEffects';

const { FiRss, FiX, FiExternalLink, FiCalendar, FiUser, FiCopy, FiCheck, FiRefreshCw } = FiIcons;

export default function RssFeedDrawer({
  isOpen,
  onClose,
  feedBookmark,
  themeColors: tc,
  isDark = true,
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (isOpen && feedBookmark?.url) {
      loadFeed(feedBookmark.url);
    }
  }, [isOpen, feedBookmark]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const loadFeed = async (url) => {
    setLoading(true);
    playSound('click');
    const res = await fetchRssFeedItems(url);
    setItems(res);
    setLoading(false);
  };

  const handleCopyHeadlines = () => {
    const text = items.map((it, idx) => `${idx + 1}. ${it.title} - ${it.link}`).join('\n');
    navigator.clipboard.writeText(text);
    setCopied(true);
    playSound('copy');
    setTimeout(() => setCopied(false), 2000);
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[95] flex justify-end">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0 bg-black/60 backdrop-blur-sm"
        />

        <motion.div
          initial={{ x: '100%' }}
          animate={{ x: 0 }}
          exit={{ x: '100%' }}
          transition={{ type: 'spring', damping: 28, stiffness: 300 }}
          className="relative w-full max-w-md h-full bg-[#111116] border-l border-[#24242d] shadow-2xl flex flex-col z-10"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-[#202028] shrink-0">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-xl bg-orange-500/10 border border-orange-500/20 flex items-center justify-center text-orange-400">
                <SafeIcon icon={FiRss} className="w-4 h-4" />
              </div>
              <div className="min-w-0">
                <h3 className="text-sm font-bold text-white truncate max-w-[240px]">
                  {feedBookmark?.title || 'Live Feed Stream'}
                </h3>
                <p className="text-[10px] text-[#777] font-mono">
                  {items.length} syndicated updates
                </p>
              </div>
            </div>

            <div className="flex items-center gap-1.5">
              <button
                onClick={() => loadFeed(feedBookmark?.url)}
                className="p-1.5 text-[#888] hover:text-white hover:bg-white/5 rounded-lg transition-colors"
                title="Refresh feed"
              >
                <SafeIcon icon={FiRefreshCw} className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
              </button>
              <button
                onClick={handleCopyHeadlines}
                className="p-1.5 text-[#888] hover:text-white hover:bg-white/5 rounded-lg transition-colors"
                title="Copy all headlines"
              >
                <SafeIcon icon={copied ? FiCheck : FiCopy} className={`w-3.5 h-3.5 ${copied ? 'text-emerald-400' : ''}`} />
              </button>
              <button
                onClick={onClose}
                className="p-1.5 text-[#888] hover:text-white hover:bg-white/5 rounded-lg transition-colors"
              >
                <SafeIcon icon={FiX} className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Feed Stream */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-20 text-[#666] text-xs gap-2">
                <SafeIcon icon={FiRefreshCw} className="w-5 h-5 animate-spin text-orange-400" />
                <span>Synchronizing live feed...</span>
              </div>
            ) : items.length === 0 ? (
              <div className="text-center py-20 text-[#666] text-xs">
                No active syndicated items found for this source.
              </div>
            ) : (
              items.map((it, idx) => (
                <div
                  key={idx}
                  className="p-3.5 rounded-xl bg-[#171720] border border-[#242430] hover:border-orange-500/30 transition-all flex flex-col gap-1.5 group"
                >
                  <div className="flex items-start justify-between gap-2">
                    <a
                      href={it.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs font-semibold text-white group-hover:text-orange-400 transition-colors leading-snug line-clamp-2"
                    >
                      {it.title}
                    </a>
                    <a
                      href={it.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-1 text-[#666] hover:text-white shrink-0"
                    >
                      <SafeIcon icon={FiExternalLink} className="w-3.5 h-3.5" />
                    </a>
                  </div>

                  {it.description && (
                    <p className="text-[11px] text-[#888] line-clamp-2 leading-relaxed">
                      {it.description}
                    </p>
                  )}

                  <div className="flex items-center gap-3 text-[10px] text-[#666] font-mono pt-1">
                    <span className="flex items-center gap-1">
                      <SafeIcon icon={FiCalendar} className="w-2.5 h-2.5" />
                      {it.pubDate}
                    </span>
                    {it.author && (
                      <span className="flex items-center gap-1">
                        <SafeIcon icon={FiUser} className="w-2.5 h-2.5" />
                        {it.author}
                      </span>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
